<script setup lang="ts">
import api from "@/app/api";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import type { User } from "@/app/services/generated/userService";
import { userService } from "@/app/services/generated/userService";
import messages, { translateWithParams } from "@/plugins/i18n";
import { computed, onBeforeMount } from "vue";
import { makeRef, withLoading } from "@/components/composables/componentTools";
import { displayEnumArray } from "@/components/mixins/DisplayHelpers";

export interface UnauthorisedComponentProps {
  requiredPermissions: readonly Readonly<EnumUserPermission>[];
}

const props = defineProps<UnauthorisedComponentProps>();
const allAdminUsers = makeRef<readonly User[]>([]);

const requiredPermissionText = computed((): string => {
  const requiredPermissionNames = displayEnumArray(props.requiredPermissions);

  return translateWithParams("user_permissions_cannot_allow_access", {
    permission_name: requiredPermissionNames,
  });
});

onBeforeMount(
  withLoading(async () => {
    const allUsers = await userService.getAllUsers(api.tenantGroupId);
    allAdminUsers.value = allUsers.filter(
      (user) =>
        user.userTenantGroupLevel.key >= 1 &&
        !user.readOnly.isSupportUser &&
        !user.readOnly.isSystemUser
    );
  })
);
</script>

<template>
  <v-container fluid class="text-center">
    <v-row justify="center">
      <v-col cols="auto">
        <v-icon class="unauthorised-icon" color="error">
          do_not_disturb_on
        </v-icon>
      </v-col>
      <v-col cols="12" class="text-h4">
        {{ messages.user_permissions_you_do_not_have_permission_to_action }}
      </v-col>
      <v-col cols="12">
        {{ requiredPermissionText }}
      </v-col>
      <v-col cols="12">
        {{ messages.user_permissions_if_you_have_questions }}
      </v-col>
      <v-col cols="12">
        {{ messages.user_permissions_your_admins_are }}
        <div v-for="user in allAdminUsers" :key="user.id.toString()">
          {{ user.name }}
        </div>
      </v-col>
    </v-row>
    <v-row justify="center" class="pt-6">
      <v-col cols="12">
        {{ messages.user_permissions_need_help }}
        <div>
          {{ $m.tel }}:
          <a :href="`tel:${$m.company_tel}`">{{ $m.company_tel }}</a>
        </div>
        <div>
          {{ $m.email }}:
          <a :href="`mailto:${$m.company_email}`">{{ $m.company_email }}</a>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="sass" scoped>
.unauthorised-icon
  font-size: 10rem
</style>
